/* UI */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';
import { Typography } from '@mui/material';

/* Shared Components */
import Image from 'application/Shared/Components/Image';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * UserGreeting component that displays a personalized greeting card with user information.
 * @returns {JSX.Element} A card containing user avatar, time-based greeting (morning, afternoon, evening) greeting, and email.
 */
export const UserGreeting = () => {
    const { t } = useTranslation();
    const session = useSession();
    const name = session?.user?.firstName || '';
    const email = session?.user?.email || '';
    const imageId = session?.user?.image;
    const hrs = new Date().getHours();
    const isFirstNameSameAsEmail = name === email;
    const displayName = isFirstNameSameAsEmail ? name.toLowerCase() : name;

    let greeting;
    if (hrs < 12) {
        greeting = displayName
            ? t('Common.greetingMorningWithName', { name: displayName })
            : t('Common.greetingMorning');
    } else if (hrs >= 12 && hrs <= 17) {
        greeting = displayName
            ? t('Common.greetingAfternoonWithName', { name: displayName })
            : t('Common.greetingAfternoon');
    } else {
        greeting = displayName
            ? t('Common.greetingEveningWithName', { name: displayName })
            : t('Common.greetingEvening');
    }

    return (
        <Card sx={{ padding: '16px' }}>
            <Flex sx={{ alignItems: 'center', gap: 2 }}>
                {imageId && (
                    <Image
                        avatar
                        imageId={imageId}
                        alt={displayName}
                        style={{ height: 48, width: 48 }}
                    />
                )}
                <Box>
                    <Typography variant="h6" sx={{ textTransform: 'none' }}>
                        {greeting}
                    </Typography>
                    {!isFirstNameSameAsEmail && (
                        <Typography variant="caption">{email}</Typography>
                    )}
                </Box>
            </Flex>
        </Card>
    );
};
